import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, MapPin, Star, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { useLists } from "@/hooks/useLists";

const DEFAULT_TYPES = ["bar", "caffetteria", "ristorante", "chiosco", "pizzeria", "gelateria", "altro"];

function Delta({ ours, theirs }) {
  if (ours == null || theirs == null) return <span className="text-neutral-400 text-xs">n/d</span>;
  const diff = ours - theirs;
  const pct = theirs ? (diff / theirs) * 100 : 0;
  if (Math.abs(pct) < 3) return <span className="text-neutral-500 text-xs inline-flex items-center gap-1"><Minus size={10} /> in linea</span>;
  const higher = diff > 0;
  return (
    <span className={`text-xs inline-flex items-center gap-1 ${higher ? "text-red-700" : "text-emerald-800"}`}>
      {higher ? <ArrowUp size={10} /> : <ArrowDown size={10} />} {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

export default function Competitors() {
  const { lists } = useLists();
  const TYPES = lists.competitor_types?.length ? lists.competitor_types : DEFAULT_TYPES;
  const [comparison, setComparison] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "bar", distance_km: 0.5, address: "", avg_espresso: 1.1, avg_cappuccino: 1.6, avg_spritz: 5.5, avg_ticket: 10, rating: 4.0, reviews_count: 50, hours: "", strengths: "", weaknesses: "", top_products: "", notes: "" });

  const load = async () => { const { data } = await api.get("/competitors/comparison"); setComparison(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/competitors", {
      ...form,
      distance_km: Number(form.distance_km), avg_espresso: Number(form.avg_espresso) || null,
      avg_cappuccino: Number(form.avg_cappuccino) || null, avg_spritz: Number(form.avg_spritz) || null,
      avg_ticket: Number(form.avg_ticket) || null, rating: Number(form.rating), reviews_count: Number(form.reviews_count),
      strengths: form.strengths.split(",").map((s) => s.trim()).filter(Boolean),
      weaknesses: form.weaknesses.split(",").map((s) => s.trim()).filter(Boolean),
      top_products: form.top_products.split(",").map((s) => s.trim()).filter(Boolean),
    });
    toast.success("Concorrente aggiunto"); setOpen(false); load();
  };
  const del = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/competitors/${id}`); load(); };

  if (!comparison) return <div className="text-neutral-500">Caricamento…</div>;
  const us = comparison.us;
  const mkt = comparison.market_avg;

  return (
    <div data-testid="competitors-page">
      <PageHeader eyebrow="Territorio Orosei" title="Analisi Concorrenza"
        subtitle="Come ti posizioni rispetto a bar, ristoranti e chioschi del territorio."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-comp-btn" className="bg-neutral-900 text-[#FDFBF7]"><Plus size={14} strokeWidth={1.5} /> Nuovo concorrente</Button></DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-serif">Nuovo concorrente</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nome</Label><Input data-testid="comp-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Tipo</Label><Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Distanza (km)</Label><Input type="number" step="0.1" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} /></div>
                <div className="col-span-2"><Label>Indirizzo</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div><Label>Espresso (€)</Label><Input type="number" step="0.10" value={form.avg_espresso} onChange={(e) => setForm({ ...form, avg_espresso: e.target.value })} /></div>
                <div><Label>Cappuccino (€)</Label><Input type="number" step="0.10" value={form.avg_cappuccino} onChange={(e) => setForm({ ...form, avg_cappuccino: e.target.value })} /></div>
                <div><Label>Spritz (€)</Label><Input type="number" step="0.10" value={form.avg_spritz} onChange={(e) => setForm({ ...form, avg_spritz: e.target.value })} /></div>
                <div><Label>Scontrino medio (€)</Label><Input type="number" step="0.5" value={form.avg_ticket} onChange={(e) => setForm({ ...form, avg_ticket: e.target.value })} /></div>
                <div><Label>Rating (0-5)</Label><Input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></div>
                <div><Label>N. recensioni</Label><Input type="number" value={form.reviews_count} onChange={(e) => setForm({ ...form, reviews_count: e.target.value })} /></div>
                <div className="col-span-2"><Label>Orari</Label><Input value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="07:00-24:00" /></div>
                <div className="col-span-2"><Label>Punti di forza (virgola)</Label><Input value={form.strengths} onChange={(e) => setForm({ ...form, strengths: e.target.value })} /></div>
                <div className="col-span-2"><Label>Punti deboli (virgola)</Label><Input value={form.weaknesses} onChange={(e) => setForm({ ...form, weaknesses: e.target.value })} /></div>
                <div className="col-span-2"><Label>Prodotti principali (virgola)</Label><Input value={form.top_products} onChange={(e) => setForm({ ...form, top_products: e.target.value })} /></div>
                <DialogFooter className="col-span-2"><Button data-testid="comp-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Salva</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        } />

      {/* Comparison */}
      <div className="border border-[#E2D9C8] rounded-lg bg-[#F3EDE1] overflow-hidden mb-8">
        <div className="p-6 border-b border-[#E2D9C8] flex items-center gap-4">
          <img src="/logo-carasau-dark.png" alt="Carasau Café" className="h-16 w-auto shrink-0" />
          <div>
            <div className="label-eyebrow">Posizionamento</div>
            <h2 className="font-serif text-3xl mt-1">vs Media Mercato</h2>
          </div>
        </div>
        <table className="w-full text-sm bg-white">
          <thead><tr className="text-left label-eyebrow border-b border-[#E2D9C8]">
            <th className="p-4">Voce</th>
            <th className="p-4 text-right">Tu</th>
            <th className="p-4 text-right">Media Orosei</th>
            <th className="p-4 text-right">Delta</th>
          </tr></thead>
          <tbody>
            <tr className="border-b border-[#E2D9C8]" data-testid="cmp-espresso"><td className="p-4">Espresso</td><td className="p-4 text-right font-medium">{us.espresso != null ? fmtEUR(us.espresso) : "—"}</td><td className="p-4 text-right">{mkt.espresso != null ? fmtEUR(mkt.espresso) : "—"}</td><td className="p-4 text-right"><Delta ours={us.espresso} theirs={mkt.espresso} /></td></tr>
            <tr className="border-b border-[#E2D9C8]" data-testid="cmp-cappu"><td className="p-4">Cappuccino</td><td className="p-4 text-right font-medium">{us.cappuccino != null ? fmtEUR(us.cappuccino) : "—"}</td><td className="p-4 text-right">{mkt.cappuccino != null ? fmtEUR(mkt.cappuccino) : "—"}</td><td className="p-4 text-right"><Delta ours={us.cappuccino} theirs={mkt.cappuccino} /></td></tr>
            <tr className="border-b border-[#E2D9C8]" data-testid="cmp-spritz"><td className="p-4">Spritz</td><td className="p-4 text-right font-medium">{us.spritz != null ? fmtEUR(us.spritz) : "—"}</td><td className="p-4 text-right">{mkt.spritz != null ? fmtEUR(mkt.spritz) : "—"}</td><td className="p-4 text-right"><Delta ours={us.spritz} theirs={mkt.spritz} /></td></tr>
            <tr data-testid="cmp-ticket"><td className="p-4">Scontrino medio</td><td className="p-4 text-right font-medium">{us.avg_ticket != null ? fmtEUR(us.avg_ticket) : "—"}</td><td className="p-4 text-right">{mkt.avg_ticket != null ? fmtEUR(mkt.avg_ticket) : "—"}</td><td className="p-4 text-right"><Delta ours={us.avg_ticket} theirs={mkt.avg_ticket} /></td></tr>
          </tbody>
        </table>
      </div>

      {/* Competitors cards */}
      <h2 className="font-serif text-2xl mb-3">Concorrenti mappati</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {comparison.competitors.map((c) => (
          <div key={c.id} className="border border-[#E2D9C8] rounded-lg p-5 bg-white" data-testid={`comp-${c.id}`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="label-eyebrow capitalize">{c.type}</span>
                  <span className="text-xs text-neutral-500 inline-flex items-center gap-1"><MapPin size={11} strokeWidth={1.5} /> {c.distance_km} km</span>
                </div>
                <div className="font-serif text-2xl mt-1">{c.name}</div>
                <div className="text-xs text-neutral-500 mt-1">{c.address}</div>
                {c.hours && <div className="text-xs text-neutral-500 mt-1">Orari: {c.hours}</div>}
              </div>
              <button onClick={() => del(c.id)} className="text-neutral-400 hover:text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button>
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2 text-xs">
              <div><div className="label-eyebrow">Espresso</div><div className="font-medium mt-1">{c.avg_espresso != null ? fmtEUR(c.avg_espresso) : "—"}</div></div>
              <div><div className="label-eyebrow">Cappuccino</div><div className="font-medium mt-1">{c.avg_cappuccino != null ? fmtEUR(c.avg_cappuccino) : "—"}</div></div>
              <div><div className="label-eyebrow">Spritz</div><div className="font-medium mt-1">{c.avg_spritz != null ? fmtEUR(c.avg_spritz) : "—"}</div></div>
              <div><div className="label-eyebrow">Scontrino</div><div className="font-medium mt-1">{c.avg_ticket != null ? fmtEUR(c.avg_ticket) : "—"}</div></div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs">
              <Star size={12} strokeWidth={1.5} className="text-amber-600 fill-amber-500" />
              <span className="font-medium">{c.rating?.toFixed(1)}</span>
              <span className="text-neutral-500">({c.reviews_count} recensioni)</span>
            </div>
            {(c.strengths?.length || c.weaknesses?.length) && (
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                {c.strengths?.length > 0 && <div><div className="label-eyebrow text-emerald-800">Forze</div><ul className="mt-1 space-y-0.5">{c.strengths.map((s) => <li key={s}>• {s}</li>)}</ul></div>}
                {c.weaknesses?.length > 0 && <div><div className="label-eyebrow text-red-800">Debolezze</div><ul className="mt-1 space-y-0.5">{c.weaknesses.map((s) => <li key={s}>• {s}</li>)}</ul></div>}
              </div>
            )}
            {c.top_products?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {c.top_products.map((p) => <span key={p} className="text-xs px-2 py-0.5 bg-[#F3EDE1] rounded-full">{p}</span>)}
              </div>
            )}
            {c.notes && <div className="mt-3 text-xs text-neutral-500 border-l-2 border-[#D7C4A3] pl-3">{c.notes}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
